import {
  CreateEquipmentInspectionChecklistMutation,
  CreateEquipmentInspectionChecklistMutationVariables,
  EquipmentInspectionChecklistCreateInput,
  EquipmentInspectionChecklistFilter,
  EquipmentInspectionChecklistsQuery,
  EquipmentInspectionChecklistsQueryVariables,
  EquipmentInspectionChecklistUpdateInput,
  UpdateEquipmentInspectionChecklistMutation,
  UpdateEquipmentInspectionChecklistMutationVariables,
} from '__generated__/graphql';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import { isNil } from 'lodash';
import { getFullNameFromMember } from 'utils/stringUtils';
import { EquipmentChecklistType } from '../EquipmentInspectionChecklistForm/EquipmentInspectionChecklistForm';
import { EquipmentInspectionChecklistTableData } from '../EquipmentInspectionChecklistTable/EquipmentInspectionChecklistTable';
import {
  CREATE_EQUIPMENT_INSPECTION_CHECKLIST_MUTATION,
  EQUIPMENT_INSPECTION_CHECKLIST_QUERY,
  UPDATE_EQUIPMENT_INSPECTION_CHECKLIST_MUTATION,
} from '../queries/equipment-inspection-checklist-queries';

export default function useEquipmentChecklists() {
  const client = useGraphQLClient();
  const pager = useGraphQLPaging();

  async function getEquipmentChecklists(
    first?: number,
    filter?: EquipmentInspectionChecklistFilter
  ): Promise<EquipmentInspectionChecklistTableData[]> {
    const result = await pager<EquipmentInspectionChecklistsQuery, EquipmentInspectionChecklistsQueryVariables>(
      {
        document: EQUIPMENT_INSPECTION_CHECKLIST_QUERY,
        variables: {
          first: first ?? 500,
          filter: {
            deletedOn: { isNull: true },
            ...filter,
          },
        },
      },
      'equipmentInspectionChecklists'
    );

    if (isNil(result)) {
      throw Error(`result was ${result}`);
    }

    return result.map((checklist) => ({
      id: checklist.id,
      checklistTitle: checklist.name,
      type: checklist.allowAttachments
        ? EquipmentChecklistType.EQUIPMENT_AND_ATTACHMENT
        : EquipmentChecklistType.EQUIPMENT_ONLY,
      inspectorId: checklist.inspectorId ?? '',
      defaultInspector: getFullNameFromMember(checklist.inspector) ?? '',
    }));
  }

  async function createEquipmentChecklist(
    input: EquipmentInspectionChecklistCreateInput
  ): Promise<EquipmentInspectionChecklistTableData> {
    const result = await client.request<
      CreateEquipmentInspectionChecklistMutation,
      CreateEquipmentInspectionChecklistMutationVariables
    >({
      document: CREATE_EQUIPMENT_INSPECTION_CHECKLIST_MUTATION,
      variables: {
        checklist: input,
      },
    });

    if (isNil(result.createEquipmentInspectionChecklist)) {
      throw Error(`result was ${result.createEquipmentInspectionChecklist}`);
    }

    return {
      id: result.createEquipmentInspectionChecklist.id,
      checklistTitle: result.createEquipmentInspectionChecklist.name,
      type: result.createEquipmentInspectionChecklist.allowAttachments
        ? EquipmentChecklistType.EQUIPMENT_AND_ATTACHMENT
        : EquipmentChecklistType.EQUIPMENT_ONLY,
      inspectorId: result.createEquipmentInspectionChecklist.inspectorId ?? '',
      defaultInspector: '',
    };
  }

  async function updateEquipmentChecklist(
    input: EquipmentInspectionChecklistUpdateInput
  ): Promise<EquipmentInspectionChecklistTableData> {
    const result = await client.request<
      UpdateEquipmentInspectionChecklistMutation,
      UpdateEquipmentInspectionChecklistMutationVariables
    >({
      document: UPDATE_EQUIPMENT_INSPECTION_CHECKLIST_MUTATION,
      variables: {
        checklist: input,
      },
    });

    if (isNil(result.updateEquipmentInspectionChecklist)) {
      throw Error(`result was ${result.updateEquipmentInspectionChecklist}`);
    }

    return {
      id: result.updateEquipmentInspectionChecklist.id,
      checklistTitle: result.updateEquipmentInspectionChecklist.name,
      type: result.updateEquipmentInspectionChecklist.allowAttachments
        ? EquipmentChecklistType.EQUIPMENT_AND_ATTACHMENT
        : EquipmentChecklistType.EQUIPMENT_ONLY,
      inspectorId: result.updateEquipmentInspectionChecklist.inspectorId ?? '',
      defaultInspector: '',
    };
  }

  return { getEquipmentChecklists, createEquipmentChecklist, updateEquipmentChecklist };
}
